import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useOnboardingWizard } from "../OnboardingWizardProvider";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
} from "lucide-react";

export function ActionConfirmationStep() {
  const { t } = useTranslation();
  const {
    stepConfig,
    formData,
    updateFormData,
    executeAction,
    goNext,
    goBack,
    markStepComplete,
    currentStep,
    onStepComplete,
    isFirstStep,
  } = useOnboardingWizard();

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [error, setError] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current || !stepConfig.action) return;
    hasRun.current = true;

    const run = async () => {
      const result = await executeAction(stepConfig.action!, { formData });
      if (result.success) {
        if (result.meta) updateFormData(result.meta);
        setStatus("success");
        markStepComplete(currentStep);
        onStepComplete(currentStep);

        // Auto-advance after a brief pause
        if (stepConfig.autoAdvance) {
          setTimeout(() => goNext(), 1500);
        }
      } else {
        setStatus("error");
        setError(result.error || t("common.error"));
      }
    };

    run();
  }, [
    stepConfig,
    formData,
    executeAction,
    goNext,
    markStepComplete,
    currentStep,
    onStepComplete,
    t,
  ]);

  const handleRetry = () => {
    hasRun.current = false;
    setStatus("loading");
    setError(null);
    // Re-trigger by resetting the ref and running manually
    const run = async () => {
      hasRun.current = true;
      const result = await executeAction(stepConfig.action!, { formData });
      if (result.success) {
        if (result.meta) updateFormData(result.meta);
        setStatus("success");
        markStepComplete(currentStep);
        onStepComplete(currentStep);
        if (stepConfig.autoAdvance) {
          setTimeout(() => goNext(), 1500);
        }
      } else {
        setStatus("error");
        setError(result.error || t("common.error"));
      }
    };
    run();
  };

  return (
    <div className="text-center space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">
          {t(stepConfig.title)}
        </h2>
        {stepConfig.subtitle && (
          <p className="text-muted-foreground">{t(stepConfig.subtitle)}</p>
        )}
      </div>

      <div className="py-8">
        {status === "loading" && (
          <div className="space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-nara-orange mx-auto" />
            <p className="text-muted-foreground">
              {stepConfig.loadingText
                ? t(stepConfig.loadingText)
                : t("common.loading")}
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4 animate-in fade-in duration-500">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
            <p className="text-foreground font-medium">
              {stepConfig.successText
                ? t(stepConfig.successText)
                : t("common.success")}
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4 animate-in fade-in duration-500">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
            <p className="text-destructive">{error}</p>
            <button
              onClick={handleRetry}
              className="px-6 py-3 bg-nara-orange text-nara-charcoal font-semibold rounded-xl hover:bg-nara-orange/90 transition-colors"
            >
              {t("common.submit")}
            </button>
          </div>
        )}
      </div>

      {/* Manual next button for non-auto-advance */}
      {status === "success" && !stepConfig.autoAdvance && (
        <button
          onClick={goNext}
          className="inline-flex items-center gap-2 px-8 py-3 bg-nara-orange text-nara-charcoal font-semibold rounded-xl hover:bg-nara-orange/90 transition-colors"
        >
          {t(stepConfig.nextLabel || "common.next")}
          <ArrowRight className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
